/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * THE BOARD SECTIONS THE ASSEMBLY CHECK READS (approved 17/10/2026, walk
 * wb0c3f810). One list, used by the assembly check (api/_critic.js) to build
 * the board it shows the model, and by the request guard (request-items.js
 * INTERNAL_NOTE_PREFIX_RE), so a section the check can name is always a
 * section the guard knows.
 *
 * Walk wb0c3f810: the check named its finding by the raw field key
 * ("objectAndPurpose") and the engine filed "objectAndPurpose: The AML
 * framework is missing ..." as a client request; the client's checklist read
 * "Please provide: objectAndPurpose.". A finding is now filed by structure: the
 * client is asked a plain question mapped from the section, never the field
 * name and never the finding, and the officer keeps the finding itself
 * (board.assemblyCheck and a case note).
 */

export const ASSEMBLY_SECTIONS = [
  'activityProfile', 'objectAndPurpose', 'sofAssessment', 'sowAssessment', 'companyDetails',
  'directors', 'ubos', 'bankingProfile', 'taxCompliance', 'ownershipChain',
];

// The officer's name for each section (case notes, never the client).
export const SECTION_LABEL = {
  activityProfile: 'Activity profile',
  objectAndPurpose: 'Object and purpose of the relationship',
  sofAssessment: 'Source of funds',
  sowAssessment: 'Source of wealth',
  companyDetails: 'Company details',
  directors: 'Directors',
  ubos: 'Beneficial owners',
  bankingProfile: 'Banking profile',
  taxCompliance: 'Tax compliance',
  ownershipChain: 'Ownership chain',
};

// The client's question for each section: plain, no field name, no finding.
export const SECTION_CLIENT_ASK = {
  activityProfile: 'Please describe your business: who your customers are, the countries you serve, and the volume of activity you expect.',
  objectAndPurpose: 'Please tell us the purpose of the account and the activity you expect to run through it.',
  sofAssessment: 'Please tell us where the funds for this relationship come from, with a document that shows it.',
  sowAssessment: 'Please tell us how the wealth of the people who own the business was built up, with a document that shows it.',
  companyDetails: "Please confirm your company's registered details: its legal name, registration number, registered address and date of incorporation.",
  directors: 'Please confirm the full names and roles of your current directors.',
  ubos: 'Please confirm who ultimately owns or controls the company, with each person\'s percentage holding.',
  bankingProfile: 'Please tell us which bank accounts the company uses and which of them this relationship will involve.',
  taxCompliance: 'Please confirm where the company is tax resident and that its tax filings are up to date.',
  ownershipChain: 'Please describe the ownership structure, showing each company between you and the people who ultimately own it.',
};

const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z]/g, '');
const BY_NORM = Object.fromEntries(ASSEMBLY_SECTIONS.flatMap(k => [[norm(k), k], [norm(SECTION_LABEL[k]), k]]));

// The section key a finding names: the key itself, the officer's label, or the
// key with spaces or underscores ("object and purpose", "object_and_purpose").
export function sectionKeyOf(section) {
  return BY_NORM[norm(section)] || null;
}

// The client's question for a finding, or null. Only the ten sections have a
// question: a section the list does not know (a model could name "redFlags" or
// "riskFactors") is never asked of the client; it goes to the officer alone
// (owner, 17/10/2026).
export function sectionClientAsk(section) {
  const key = sectionKeyOf(section);
  return key ? SECTION_CLIENT_ASK[key] : null;
}

// What the officer reads beside a finding whose section is not one of the ten.
export const UNRECOGNISED_SECTION_NOTE = "section not recognised, not asked of the client, needs the officer's decision";

// A raw "section: ..." line: the shape the engine wrote before this change
// (and the shape v1_718's generator notes take). The section key is returned
// when the line opens with one of the ten keys.
const RAW_LINE_RE = new RegExp('^\\s*(' + ASSEMBLY_SECTIONS.join('|') + ')\\b\\s*(?:\\/\\s*[a-zA-Z]+\\s*)*(?:\\([^)]*\\))?\\s*:');
export function rawSectionLineKey(line) {
  const m = typeof line === 'string' ? line.match(RAW_LINE_RE) : null;
  return m ? m[1] : null;
}

// The findings as document gaps. documentGaps holds strings only (the schema,
// and every email, prompt and list that prints it), so the gap is the client's
// question itself; the finding stays on board.assemblyCheck for the officer,
// and assemblyCheck.asks links each question to its section. One question per
// section, never repeating one already on the list. A finding the client gave
// in conversation is not asked (the merge absorbs it on the next pass).
// A raw line an earlier run wrote for a section the check has findings for
// (this run's or the stored ones) is replaced by that section's question, so
// it is neither shown nor silently lost. A finding whose section is not one of
// the ten is never a gap; it is returned in `unrecognised` for the officer
// (board.assemblyCheck and the case note). Returns { gaps, asks, unrecognised }.
export function addAssemblyGaps(gaps, missed, priorMissed) {
  const out = (Array.isArray(gaps) ? gaps : []).slice();
  const asks = [];
  const unrecognised = (missed || []).filter(m => m && m.section && !sectionKeyOf(m.section));
  const flagged = new Set([...(missed || []), ...(priorMissed || [])].filter(m => m && m.section).map(m => sectionKeyOf(m.section)).filter(Boolean));
  const add = (section) => {
    const ask = sectionClientAsk(section);
    if (!ask) return;
    if (!asks.some(a => a.ask === ask)) asks.push({ section: sectionKeyOf(section), ask });
    if (!out.some(g => String(g) === ask)) out.push(ask);
  };
  for (let i = out.length - 1; i >= 0; i--) {
    const key = rawSectionLineKey(out[i]);
    if (key && flagged.has(key)) { out.splice(i, 1); add(key); }
  }
  for (const m of (missed || [])) {
    if (!m || !m.section || m.source === 'transcript' || !sectionKeyOf(m.section)) continue;
    add(m.section);
  }
  return { gaps: out, asks, unrecognised };
}

// What the officer reads: each finding with its section's name.
export function assemblyFindingsNote(missed) {
  const list = (missed || []).filter(Boolean);
  return list.map(m => {
    const key = sectionKeyOf(m.section);
    const tail = !key ? ` (${UNRECOGNISED_SECTION_NOTE})` : m.source === 'transcript' ? ' (said in conversation; absorbed on the next pass, not asked of the client)' : '';
    return `${key ? SECTION_LABEL[key] : String(m.section || 'Unnamed section')}: ${String(m.substance || '').slice(0, 400)}${tail}`;
  }).join(' ');
}
